import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import { Badge, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import moment from "moment-timezone"
import { post } from "helpers/api_helper"
import { refusalText } from "helpers/apiRefusal"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { FORBIDDEN_TEXT, SESSION_EXPIRED_TEXT } from "../../competitionOperation"
import { actionLabel, amountOf, moneyText, reasonText, requestRefusalText } from "./entryOperationText"
import { resolutionOutcomes } from "../../withdrawalFinanceText"
import { accountNumber, unnamedParticipant } from "../../components/participantIdentity"
import DraftModal from "../../../Logistics/DraftModal"
import "./entryOperations.scss"

// Administrative adds, withdrawals and waiting-list withdrawals that Payments has
// not settled yet, with Retry and Close, and a SuperAdmin's Refund Resolution for a
// refund no Retry can credit (API ADR 0014). The list comes from the same
// entry-context read as the admission buttons; only a command in flight listens to
// the socket, for its own answer.
const WAIT_MS = 60000
const closeEffects = {
  cancelAndRefund: amount => `The add is cancelled and its reservation removed. If${amount || " the fee"} was taken from the payer's wallet, it is refunded.`,
  cancel: () => "The add is cancelled and its reservation removed. No money was charged.",
  cancelWithdrawal: () => "The withdrawal is cancelled. The entry stays confirmed and nothing is refunded.",
}
const commands = {
  retry: { label: "Retry", sent: who => `Retry sent for ${who}. The list refreshes as Payments answers.`, applied: who => `Retry applied for ${who}. The list refreshes as Payments answers.` },
  close: { label: "Close", sent: who => `Close sent for ${who}. The operation leaves this list once its money is settled.`,
    applied: who => `Close applied for ${who}. The operation leaves this list once its money is settled.` },
  resolve: { label: "Resolution", sent: who => `Resolution sent for ${who}.`, applied: who => `Refund resolved for ${who}. The operation leaves this list.` },
}
const RESOLVE_HINT = "Retry cannot credit this refund. Resolve it after checking the payer's wallet."
const emptyDraft = { key: null, decision: "", note: "" }
const states = { inProgress: ["light", "In progress"], retrying: ["warning", "Retrying automatically"], needsAttention: ["danger", "Needs attention"] }
const stopRun = run => {
  const current = run.current
  run.current = null
  current?.socket?.off("events-response", current.receive)
  clearTimeout(current?.deadline)
  return current
}
// A Tenis10 child's operation lists the child, not the parent's account it rides on: siblings share that account, so a child is
// named with its own number, as its entry log row is.
const personName = person => {
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ")
  if (!name) return unnamedParticipant(person) || "Participant unavailable"
  return person.profileId && accountNumber(person) != null ? `${name} #${accountNumber(person)}` : name
}
const names = operation => operation.participants?.map(personName).join(" / ") || "Participants unavailable"

export default function EntryOperations({ eventId, competitionId, operations, timezone, onRetryLoad, onChanged, onSettled }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore) || {}
  const [pending, setPending] = useState(null)
  const [notice, setNotice] = useState(null)
  const [closing, setClosing] = useState(null)
  // The Refund Resolution dialog keeps the operation's key, takes its target and
  // choices from the current read, and keeps its draft until it is discarded or sent.
  const [resolveKey, setResolveKey] = useState(null)
  const [draft, setDraft] = useState(emptyDraft)
  const resolving = resolveKey && Array.isArray(operations) ? operations.find(operation => operation.key === resolveKey && operation.actions?.resolve?.allowed) : null
  const timers = useRef([])
  // The command in flight: one at a time, its buttons disabled until Events
  // answers it on the socket, a later read shows its row changed, or 60 s pass.
  const run = useRef(null)
  const settle = useCallback(next => { if (stopRun(run)) { setPending(null); setNotice(next) } }, [])
  useEffect(() => () => { timers.current.forEach(clearTimeout); stopRun(run) }, [])
  // An operation that leaves the list changed the entries: the lists read again.
  const shown = useRef(null)
  const keys = Array.isArray(operations) ? operations.map(operation => operation.key).join(",") : null
  useEffect(() => {
    if (keys === null) return
    const previous = shown.current
    shown.current = keys
    if (previous !== null && previous.split(",").some(key => key && !keys.split(",").includes(key))) onSettled?.()
  }, [keys, onSettled])
  useEffect(() => {
    const current = run.current
    if (!current || !Array.isArray(operations)) return
    const row = operations.find(operation => operation.key === current.key)
    if (!row || JSON.stringify(row) !== current.row) settle({ tone: "info", text: `${current.label} for ${current.who}: the list shows its latest state.` })
  }, [operations, settle])

  const openResolve = operation => { setResolveKey(operation.key); setDraft(current => current.key === operation.key ? current : { ...emptyDraft, key: operation.key }) }
  const act = async (operation, action, details = {}) => {
    if (run.current) return
    setClosing(null)
    setResolveKey(null)
    const correlationId = newPlayerOperationId()
    const command = commands[action], who = names(operation)
    const receive = message => {
      if (message?.operation !== "entry-recovery" || message.correlationId !== correlationId || message.operationKey !== operation.key) return
      if (message.success === true) {
        settle({ tone: "info", text: command.applied(who) })
        if (action === "resolve") setDraft(emptyDraft)
      } else if (message.success === false && message.retryable === false) settle({ tone: "danger", text: `${command.label} was refused: ${requestRefusalText(message.message)}` })
      else return
      onChanged?.()
    }
    run.current = { key: operation.key, row: JSON.stringify(operation), label: command.label, who, socket, receive,
      deadline: setTimeout(() => settle({ tone: "muted", text: `No answer yet for the ${command.label} of ${who}. The list shows the latest state and Action Logs records the result.` }), WAIT_MS) }
    socket?.on("events-response", receive)
    setPending(operation.key)
    setNotice({ tone: "info", text: command.sent(who) })
    try {
      await post(`/admin/open-entry-operations/${operation.key}`, { eventId, competitionId, action, ...details, ...(socketClientId && { socketClientId }) },
        { headers: { "X-Correlation-Id": correlationId } })
      // The command is queued: read again while it is processed.
      timers.current.push(...[2000, 6000, 15000, 30000].map(delay => setTimeout(() => onChanged?.(), delay)))
    } catch (error) {
      // The route's session and role gates (checkAllowedRoles: 401 SESSION_EXPIRED, 403 FORBIDDEN) and a 4xx code this panel
      // words say why; a masked 5xx, no answer or a validation sentence says what to do next.
      const status = error?.response?.status
      const reason = status === 403 && error.response.data?.message === "FORBIDDEN" ? FORBIDDEN_TEXT : refusalText(error, reasonText, null)
      settle({ tone: "danger", text: status === 401 ? `${command.label}: ${SESSION_EXPIRED_TEXT}`
        : reason ? `${command.label} was not sent: ${reason}`
          : `${command.label} could not be sent right now. Reload Operations in progress and try again.` })
    }
  }
  const explanation = draft.note.trim()

  return <section className="entry-operations" aria-labelledby="entry-operations-heading">
    <h3 id="operations" className="h5" tabIndex={-1}><span id="entry-operations-heading">Operations in progress</span></h3>
    {notice && <p role="status" className={`text-${notice.tone}`}>{notice.text}</p>}
    {operations === undefined && <p className="text-muted">Checking operations in progress…</p>}
    {operations === null && <p role="alert" className="text-danger">Operations in progress could not be loaded.
      <Button color="link" onClick={onRetryLoad}>Retry</Button></p>}
    {Array.isArray(operations) && !operations.length && <p className="text-muted">No operations in progress.</p>}
    {Array.isArray(operations) && operations.length > 0 && <ul className="entry-operations-list">{operations.map(operation => {
      const [color, label] = states[operation.state] || states.inProgress
      const retry = operation.actions?.retry || {}, close = operation.actions?.close || {}, resolve = operation.actions?.resolve
      const hint = resolve ? (resolve.allowed ? RESOLVE_HINT : reasonText(resolve.code))
        : !retry.allowed || !close.allowed ? reasonText((!close.allowed ? close : retry).code) : null
      const at = operation.createdAt && moment(operation.createdAt).isValid() ? moment(operation.createdAt).tz(timezone || "UTC").format("DD MMM YYYY, HH:mm") : "Time unavailable"
      return <li className="entry-operation-row" key={operation.key}>
        <div className="entry-operation-main">
          <strong>{names(operation)}</strong>
          <span>{actionLabel(operation)}</span>
          <Badge color={color}>{label}{operation.state === "retrying" && operation.autoRetries ? ` (${operation.autoRetries})` : ""}</Badge>
        </div>
        <div className="entry-operation-details">
          <span>{moneyText(operation)}</span>
          {operation.attention?.reason && <span className="text-danger">{reasonText(operation.attention.reason)}</span>}
          {operation.message && <span>{operation.sporting === "notApplied" ? "Entry not applied: " : ""}{reasonText(operation.message)}</span>}
          {operation.reason && <span className="text-muted">Reason: {operation.reason}</span>}
          <time className="text-muted" dateTime={operation.createdAt}>Started {at}</time>
        </div>
        <div className="entry-operation-actions">
          <span title={retry.allowed ? undefined : reasonText(retry.code)}>
            <Button size="sm" color="outline-primary" disabled={!retry.allowed || Boolean(pending)} onClick={() => act(operation, "retry")}>Retry</Button>
          </span>
          {resolve ? <span title={resolve.allowed ? undefined : reasonText(resolve.code)}>
            <Button size="sm" color="light" disabled={!resolve.allowed || Boolean(pending)} onClick={() => openResolve(operation)}>Resolve…</Button>
          </span> : <span title={close.allowed ? undefined : reasonText(close.code)}>
            <Button size="sm" color="light" disabled={!close.allowed || Boolean(pending)} onClick={() => setClosing(operation)}>Close</Button>
          </span>}
          {hint && <small className="text-muted d-block">{hint}</small>}
        </div>
      </li>
    })}</ul>}
    <Modal isOpen={Boolean(closing)} toggle={() => setClosing(null)}>
      <ModalHeader toggle={() => setClosing(null)}>Close this operation?</ModalHeader>
      <ModalBody>
        {closing && <><p><strong>{names(closing)}</strong> · {actionLabel(closing)}</p>
          <p>{(closeEffects[closing.actions?.close?.effect] || closeEffects.cancel)(amountOf(closing.money))}</p>
          <p className="text-muted mb-0">The result is recorded in Action Logs.</p></>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={() => setClosing(null)}>Cancel</Button>
        <Button color="primary" disabled={!closing || Boolean(pending)} onClick={() => act(closing, "close")}>Close operation</Button>
      </ModalFooter>
    </Modal>
    {resolving && <DraftModal id="refund-resolution-title" title="Resolve this refund?" dirty={Boolean(draft.decision || explanation)}
      onClose={() => { setResolveKey(null); setDraft(emptyDraft) }}
      actions={<Button color="primary" className="ml-2" disabled={Boolean(pending) || !draft.decision || explanation.length < 10}
        onClick={() => act(resolving, "resolve", { resolution: draft.decision, note: explanation })}>Resolve refund</Button>}>
      <ModalBody>
        <p><strong>{names(resolving)}</strong> · {actionLabel(resolving)}</p>
        <p className="text-danger">{reasonText(resolving.attention?.reason)}</p>
        <FormGroup tag="fieldset">
          <legend className="col-form-label pt-0">Decision</legend>
          {Object.entries(resolutionOutcomes).map(([value, text]) => <FormGroup check key={value}>
            <Input id={`refund-resolution-${value}`} type="radio" name="refund-resolution" checked={draft.decision === value} onChange={() => setDraft(current => ({ ...current, decision: value }))} />
            <Label check for={`refund-resolution-${value}`}>{text}</Label>
          </FormGroup>)}
        </FormGroup>
        <FormGroup>
          <Label for="refund-resolution-note">Explanation (required)</Label>
          <Input id="refund-resolution-note" type="textarea" rows={3} maxLength={2000} value={draft.note} onChange={event => { const note = event.target.value; setDraft(current => ({ ...current, note })) }} />
          <small className="text-muted">At least 10 characters: where the money went, or why none is owed.</small>
        </FormGroup>
        <p className="mb-0">This records a decision. It moves no money. If money is owed, credit it first with Wallet adjustment. The decision is recorded in Action Logs.</p>
      </ModalBody>
    </DraftModal>}
  </section>
}
